import React, { useContext } from "react";
import { generateId } from "./../../ricos-content/index.js";
import { EditorContext, ModalServiceContext, RicosContext } from "./../../ricos-context/index.js";
import { HTMLData_Source } from "./../../ricos-schema/next-schema/index.js";
import { FloatingContainer } from "./../../_shared/floating-container.js";
import { PromptInputArea } from "./../../_shared/prompt-input-area/prompt-input-area.js";
import { useRequestService } from "./../../_shared/request-service-provider/request-service-provider.js";
import { changeSelectionToEndOfTextBlock, uuid } from "./../../wix-rich-content-common/index.js";
import { useHtmlPluginContext } from "../context.js";
import { HTML_TYPE } from "../types.js";
import { WIDGET_TYPE_OPTIONS } from "./common.js";
import { doPrompt } from "./do-prompt.js";
import { WidgetTypeSelector } from "./widget-type-selector/widget-type-selector.js";
const FloatingPromptInput = ({
  nodeId: initialNodeId,
  ...props
}) => {
  const { makeRequest } = useRequestService();
  const {
    adapter: { tiptapEditor },
    getPublicCommands
  } = React.useContext(EditorContext);
  const { t } = useContext(RicosContext);
  const publicCommands = getPublicCommands();
  const ref = React.useRef(null);
  const modalService = useContext(ModalServiceContext);
  const { state, setState } = useHtmlPluginContext();
  const [nodeId] = React.useState(() => initialNodeId ?? generateId());
  React.useEffect(() => {
    setTimeout(() => {
      ref.current?.focus();
    }, 0);
  }, []);
  return /* @__PURE__ */ React.createElement(
    FloatingContainer,
    {
      ...props,
      state: "overlay",
      editorContainer: tiptapEditor.options.element,
      onClickOutside: (e) => {
        const target = e.target;
        if (ref.current && ref.current?.contains?.(target)) {
          return;
        }
        const targetElement = target instanceof Element ? target : null;
        const portalElement = targetElement?.closest('[data-hook*="ricos-portal"]');
        if (portalElement) {
          return;
        }
        const popoverContent = targetElement?.closest('[data-hook*="popover-content"]');
        if (popoverContent) {
          return;
        }
        modalService.closeModal("FLOATING-PROMPT-INPUT");
      }
    },
    /* @__PURE__ */ React.createElement(
      PromptInputArea,
      {
        placeholder: t("HtmlPlugin_AiWidget_Input_Placeholder_Text"),
        ref,
        zIndex: 1e3,
        maxLength: 1e4,
        forceMultiline: true,
        dataHook: "prompt-input-area",
        value: state[nodeId]?.promptText ?? "",
        onChange: (text) => {
          setState((prev) => ({
            ...prev,
            [nodeId]: {
              ...prev[nodeId],
              status: "IDLE",
              promptText: text
            }
          }));
        },
        onSubmit: async ({ text }) => {
          changeSelectionToEndOfTextBlock(tiptapEditor);
          modalService.closeModal("FLOATING-PROMPT-INPUT");
          publicCommands.insertBlockWithBlankLines(HTML_TYPE, {
            id: nodeId,
            containerData: {
              alignment: "CENTER",
              height: {
                custom: 474
              },
              width: {
                custom: 900
              }
            },
            loading: true,
            source: HTMLData_Source.AI
          });
          const currentRequestId = uuid();
          setState((prev) => ({
            ...prev,
            [nodeId]: {
              ...prev[nodeId],
              status: "LOADING",
              requestId: currentRequestId,
              promptText: text
            }
          }));
          const document = getPublicCommands().getContent();
          const response = await doPrompt({
            prompt: text,
            document,
            selection: getPublicCommands().getSelectionSlice() ?? [],
            makeRequest,
            widgetType: WIDGET_TYPE_OPTIONS.find(
              (option) => option.id === state[nodeId]?.selectedWidgetTypeId
            )?.label
          });
          setState((prev) => {
            const nodeState = prev[nodeId];
            if (nodeState?.status === "LOADING" && nodeState.requestId === currentRequestId && response.type === "SUCCESS") {
              tiptapEditor.commands.updateNodeAttrsById(nodeId, {
                loading: false,
                html: response.html,
                containerData: response.containerData
              });
              return {
                ...prev,
                [nodeId]: {
                  ...prev[nodeId],
                  status: "SUCCESS",
                  html: response.html,
                  promptText: ""
                }
              };
            }
            if (nodeState?.status === "LOADING" && nodeState.requestId === currentRequestId && response.type === "ERROR") {
              tiptapEditor.commands.updateNodeAttrsById(nodeId, {
                loading: false,
                error: response.error
              });
              return {
                ...prev,
                [nodeId]: {
                  ...prev[nodeId],
                  status: "ERROR",
                  error: response.error,
                  promptText: nodeState.promptText
                }
              };
            }
            return prev;
          });
        },
        minHeight: "170px",
        actionBarComponent: /* @__PURE__ */ React.createElement(
          WidgetTypeSelector,
          {
            options: WIDGET_TYPE_OPTIONS,
            selectedId: state[nodeId]?.selectedWidgetTypeId,
            onSelect: (typeId) => {
              setState((prev) => ({
                ...prev,
                [nodeId]: { ...prev[nodeId], selectedWidgetTypeId: typeId }
              }));
            },
            onRemove: () => {
              setState((prev) => ({
                ...prev,
                [nodeId]: { ...prev[nodeId], selectedWidgetTypeId: void 0 }
              }));
            }
          }
        )
      }
    )
  );
};
export {
  FloatingPromptInput
};
//# sourceMappingURL=floating-prompt-input.js.map
